// frontend/src/App.js
//interface

import React, { useState, useEffect } from 'react';
import { Upload, TrendingUp, BarChart3, FileText, Mail, Download, Loader, CheckCircle, AlertCircle, X, Lock, Zap, CreditCard } from 'lucide-react';
import axios from 'axios';
import './App.css';

// Auto-detect API URL based on environment
const API_URL = (() => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) return envUrl; // Use explicit env var if set
  
  // Local development detection
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:5000';
  }
  
  // Production: use relative path (same domain)
  return '';
})();

function TradeAnalyticsPlatform() {
  const [activePage, setActivePage] = useState('home');
  const [navOpen, setNavOpen] = useState(false); // Hamburger menu state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [analysisType, setAnalysisType] = useState('trading');
  const [isProcessing, setIsProcessing] = useState(false);
  const [pdfReady, setPdfReady] = useState(false);
  const [pdfFilename, setPdfFilename] = useState('');
  const [error, setError] = useState(null);
  const [gradientPosition, setGradientPosition] = useState(0);
  const [processingStatus, setProcessingStatus] = useState('');
  const [expandedAboutCard, setExpandedAboutCard] = useState(null);
  const [expandedFAQItem, setExpandedFAQItem] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactMessage, setContactMessage] = useState(null);
  const [selectedSample, setSelectedSample] = useState(null);
  const [sampleFullscreen, setSampleFullscreen] = useState(false);

  const seoPages = new Set(['mt5-permutation-testing', 'metatrader-5-analytics']);

  const pageFromPath = (pathname) => {
    const cleanPath = pathname.replace(/\/+$/, '') || '/';

    switch (cleanPath) {
      case '/':
        return 'home';
      case '/products':
        return 'products';
      case '/about':
        return 'about';
      case '/faq':
        return 'faq';
      case '/samples':
        return 'samples';
      case '/upload':
        return 'upload';
      case '/contact':
        return 'contact';
      case '/policies':
        return 'policies';
      case '/mt5-permutation-testing':
        return 'mt5-permutation-testing';
      case '/metatrader-5-analytics':
        return 'metatrader-5-analytics';
      default:
        return 'home';
    }
  };

  const setProduct = (type) => {
    setAnalysisType(type);
    setPdfReady(false);
    setUploadedFile(null);
    setPdfFilename('');
    setError(null);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setGradientPosition(prev => (prev + 1) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setActivePage(pageFromPath(window.location.pathname));
  }, []);

  useEffect(() => {
    const onPopState = () => {
      setActivePage(pageFromPath(window.location.pathname));
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);


  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.csv')) {
      setUploadedFile(file);
      setError(null);
      setPdfReady(false);
    } else {
      setError('Please upload a valid CSV file');
    }
  };

  const processAndGeneratePDF = async () => {
    if (!uploadedFile) {
      setError('Please upload a file first');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setProcessingStatus('Uploading file...');

    const formData = new FormData();
    formData.append('file', uploadedFile);
    formData.append('analysis_type', analysisType);

    try {
      setProcessingStatus('Analyzing data...');
      const response = await axios.post(`${API_URL}/api/analyze`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setPdfFilename(response.data.pdf_filename);
        setPdfReady(true);
        setProcessingStatus('Complete!');
      } else {
        setError(response.data.message || 'Analysis failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error processing file: ' + err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/download/${pdfFilename}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', pdfFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Error downloading PDF: ' + (err.response?.data?.message || err.message));
    }
  };

  const resetUpload = () => {
    setUploadedFile(null);
    setPdfReady(false);
    setError(null);
    setPdfFilename('');
    setProcessingStatus('');
  };

  const handleNavClick = (page) => {
    const path = page === 'home' ? '/' : `/${page}`;
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
    }
    setActivePage(page);
    setNavOpen(false); // Close menu on link click (mobile)
  };

  const navActivePage = seoPages.has(activePage) ? 'products' : activePage;

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    setContactMessage(null);

    try {
      const response = await axios.post(`${API_URL}/api/contact`, {
        name: contactForm.name,
        email: contactForm.email,
        message: contactForm.message,
      });

      if (response.data.success) {
        setContactMessage({ type: 'success', text: response.data.message });
        setContactForm({ name: '', email: '', message: '' });
      } else {
        setContactMessage({ type: 'error', text: response.data.message });
      }
    } catch (err) {
      setContactMessage({ type: 'error', text: err.response?.data?.message || 'Error sending message. Please try again.' });
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <div className="app">
      <nav className="nav-bar">
        <div className="nav-container">
          <div 
            className="nav-logo" 
            onClick={() => handleNavClick('home')}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            <TrendingUp className="logo-icon" size={32} />
            <span className="logo-text">AnalyticClarity</span>
          </div>
          {/* Hamburger icon for mobile */}
          <button
            className={`hamburger-menu${navOpen ? ' open' : ''}`}
            aria-label="Toggle navigation menu"
            onClick={() => setNavOpen((open) => !open)}
            type="button"
          >
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect y="5" width="28" height="3.5" rx="1.5" fill="#fff" />
              <rect y="12" width="28" height="3.5" rx="1.5" fill="#fff" />
              <rect y="19" width="28" height="3.5" rx="1.5" fill="#fff" />
            </svg>
          </button>
          <div className={`nav-links${navOpen ? ' nav-open' : ''}`}>
            {['home', 'products', 'about', 'faq', 'samples', 'upload', 'contact', 'policies'].map(page => (
              <button
                key={page}
                onClick={() => handleNavClick(page)}
                className={`nav-link ${navActivePage === page ? 'active' : ''}`}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                {page === 'policies' ? 'Policies' : page.charAt(0).toUpperCase() + page.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {activePage === 'home' && (
        <div className="page">
          <div className="hero-section">
            <div 
              className="hero-background"
              style={{
                backgroundImage: `linear-gradient(${gradientPosition}deg, rgba(3, 7, 18, 0.15), rgba(3, 7, 18, 0.05)), url(${process.env.PUBLIC_URL + '/landscape.png'})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center 50%',
                backgroundRepeat: 'no-repeat'
              }}
            />
            <div className="hero-content">
              {/* Background image is applied via .hero-background; front page is informational, no overlay content */}
            </div>
          </div>

          <div className="section">
            <h2 className="section-title">How It Works</h2>
            <div className="steps-grid">
              {[
                { step: '1', icon: <Upload size={40} />, title: 'Upload CSV', desc: 'Drop your investment history or brokerage export file' },
                { step: '2', icon: <BarChart3 size={40} />, title: 'Deep Analysis', desc: 'Our engine performs trade attribution, behavioral diagnostics, and Monte Carlo simulations' },
                { step: '3', icon: <FileText size={40} />, title: 'Download Report', desc: 'Receive a professional PDF with charts, insights, and optimization recommendations' }
              ].map((item, idx) => (
                <div key={idx} className="step-card">
                  <div className="step-number">{item.step}</div>
                  <div className="step-icon">{item.icon}</div>
                  <h3 className="step-title">{item.title}</h3>
                  <p className="step-desc">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activePage === 'about' && (
        <div className="page">
          <h1 className="page-title">About AnalyticClarity</h1>
          <div className="about-intro-section">
            <p className="about-intro-text">
              We are a data-driven analytics company focused on putting institutional-grade performance and risk analysis into the hands of individual traders, investors, and small teams. Our goal is simple: help market participants understand whether their performance reflects a real, repeatable edge or short-term randomness — using the same analytical principles applied in professional and institutional environments.
            </p>
          </div>

          <div className="about-cards-container">
            {[
              {
                id: 'different',
                title: 'What Makes Us Different',
                icon: <Lock size={32} />,
                content: [
                  'Unlike most platforms, we never ask for access to your trading accounts, usernames, passwords, or personal credentials.',
                  'Instead, we work exclusively with downloadable transaction history files (such as MetaTrader trade reports).',
                  'This approach:',
                  '• Protects your privacy',
                  '• Eliminates security risk',
                  '• Allows for fully custom, account-specific analysis',
                  '• Avoids broker or platform lock-in',
                  'Your data stays yours — we analyze results, not accounts.',
                  '',
                  'Our analysis is strictly evidence-based and honest.',
                  'We do not optimize, curve-fit, or soften conclusions.',
                  'If a strategy shows fragility, randomness, or negative expectancy, we state this clearly and explain why — even when the results are uncomfortable.'
                ]
              },
              {
                id: 'analytics',
                title: 'Institutional-Level Analytics, Simplified',
                icon: <BarChart3 size={32} />,
                content: [
                  'We go far beyond standard platform reports and basic performance metrics.',
                  'Depending on your use case, our reports include:',
                  '• Deep performance and risk diagnostics',
                  '• Clear, interpretable visualizations',
                  '• Portfolio-level insights and optimization (for investors)',
                  '• Robustness and survivability testing (for traders), including:',
                  '  - Permutation and trade-removal tests',
                  '  - Monte Carlo simulations',
                  '  - Cost, slippage, and stress sensitivity analysis',
                  'The focus is not on signals or predictions — but on evidence, robustness, and long-term sustainability.'
                ]
              },
              {
                id: 'who',
                title: 'Who This Is For',
                icon: <TrendingUp size={32} />,
                content: [
                  'Our analytics are designed for:',
                  '• Serious individual traders',
                  '• Long-term investors',
                  '• Algo and system traders',
                  '• Prop-firm candidates',
                  '• Small trading teams and businesses',
                  'If you care about risk, drawdowns, and whether your strategy actually works, this platform is built for you.'
                ]
              }
            ].map((card) => (
              <div
                key={card.id}
                className={`about-card ${expandedAboutCard === card.id ? 'expanded' : ''}`}
                onClick={() => setExpandedAboutCard(expandedAboutCard === card.id ? null : card.id)}
                style={{ cursor: 'pointer' }}
              >
                <div className="about-card-header">
                  <div className="about-card-icon">{card.icon}</div>
                  <h3 className="about-card-title">{card.title}</h3>
                  <div className="about-card-toggle">
                    {expandedAboutCard === card.id ? '−' : '+'}
                  </div>
                </div>
                <div className="about-card-content">
                  {card.content.map((line, idx) => (
                    <p key={idx} className={line.startsWith('•') || line.startsWith('-') ? 'about-bullet' : ''}>
                      {line}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activePage === 'faq' && (
        <div className="page">
          <h1 className="page-title">Frequently Asked Questions</h1>
          <div className="faq-intro-section">
            <p className="faq-intro-text">
              Find answers to common questions about AnalyticClarity, supported formats, data privacy, and how our analytics work.
            </p>
          </div>

          <div className="faq-container">
            {[
              {
                id: 'formats',
                question: 'What file formats and brokers do you support?',
                answer: [
                  'We currently support CSV files exported directly from your broker or trading platform.',
                  '• MetaTrader 5 (MT5) - Fully Supported',
                  'Have a different broker? Contact us with a sample export file (anonymized) and we can prioritize adding support for it.'
                ]
              },
              {
                id: 'privacy',
                question: 'How do you handle my data? Is it private?',
                answer: [
                  'Your data privacy and security are our top priorities.',
                  '• We never store your CSV files on our servers.',
                  '• We analyze files in-memory and delete them immediately after processing.',
                  '• We never share or sell your data to third parties.',
                  '• We do not require access to your trading accounts, usernames, or passwords.',
                  '• Your downloaded PDF report remains entirely under your control.'
                ]
              },
              {
                id: 'upload',
                question: 'What data fields are required for analysis?',
                answer: [
                  'Minimum required fields:',
                  '• Trade ID or ticket number',
                  '• Symbol or instrument',
                  '• Side (Buy/Sell or Long/Short)',
                  '• Open Time',
                  '• Close Time',
                  '• Open Price',
                  '• Close Price',
                  '• Volume or Lot Size',
                  '• Net P&L (or ability to calculate it)',
                  'Optional fields (recommended for richer analysis):',
                  '• Stop Loss and Take Profit levels',
                  '• Commission',
                  '• Swaps or Interest',
                  '• Position Size',
                  'Contact us if you need help formatting your data.'
                ]
              },

              {
                id: 'frequency',
                question: 'How often should I run analysis?',
                answer: [
                  'The frequency depends on your goals and trading frequency:',
                  '• Active traders (daily/weekly): Monthly or quarterly reviews recommended',
                  '• Swing traders: Quarterly or after 50+ new trades',
                  '• Long-term investors: Annually or after significant market changes',
                  '• After major changes: Run analysis immediately after strategy modifications',
                  'Regular analysis helps you catch statistical drift early and validate that your edge remains intact over time.',
                  'Think of it as your trading health check.'
                ]
              },
              {
                id: 'monte-carlo',
                question: 'What are Monte Carlo simulations and why should I care?',
                answer: [
                  'Monte Carlo simulations test how your strategy might perform under different random sequences of your trades.',
                  '• They show a range of possible outcomes based on your historical trade results',
                  '• Helps identify if your strategy depends on getting "lucky" with trade order',
                  '• Reveals worst-case drawdown scenarios (important for risk management)',
                  '• Validates whether your edge is robust or fragile',
                  'Example: If your strategy can only profit in one specific sequence, that\'s a red flag. Our tests reveal this.',
                  'A robust strategy shows consistent profitability across most simulated sequences.'
                ]
              },
              {
                id: 'support',
                question: 'What if I have issues uploading or generating a report?',
                answer: [
                  'Common troubleshooting steps:',
                  '• Verify your CSV is UTF-8 encoded (not Excel default)',
                  '• Ensure all required fields are present and non-empty',
                  '• Check that numerical fields (prices, volumes) don\'t have special characters',
                  '• Try exporting from your broker again with all trades included',
                  'If issues persist, use the Contact Us page to reach out with your sample file and error message.',
                  'We\'ll help debug and add support if your broker format needs adjustment.'
                ]
              }
            ].map((item) => (
              <div
                key={item.id}
                className={`faq-item ${expandedFAQItem === item.id ? 'expanded' : ''}`}
                onClick={() => setExpandedFAQItem(expandedFAQItem === item.id ? null : item.id)}
                style={{ cursor: 'pointer' }}
              >
                <div className="faq-question-header">
                  <h3 className="faq-question">{item.question}</h3>
                  <div className="faq-toggle">
                    {expandedFAQItem === item.id ? '−' : '+'}
                  </div>
                </div>
                <div className="faq-answer">
                  {item.answer.map((line, idx) => (
                    <p key={idx} className={line.startsWith('•') || line.startsWith('-') ? 'faq-bullet' : ''}>
                      {line}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activePage === 'products' && (
        <div className="page">
          <h1 className="page-title">Advanced Trading & Portfolio Analytics</h1>
          <div className="products-grid" style={{ justifyContent: 'center', maxWidth: '1650px', margin: '0 auto', display: 'flex', flexWrap: 'nowrap', gap: '20px' }}>
            <div className="product-card product-b" style={{ flex: '1', minWidth: '500px', maxWidth: '520px' }}>
              <div className="product-header">
                <BarChart3 className="product-icon" size={48} />
                <h2 className="product-name">Active Trading Analytics</h2>
              </div>
              <h3 className="product-title">Advanced Trading Performance Reports</h3>
              <p className="product-desc">
                Advanced statistical analysis for active traders and prop firms using MetaTrader 5 data. 
                Includes rigorous statistical testing, trade diagnostics, and robustness validation.
              </p>
              <div className="product-features">
                <h4>Enhanced Features:</h4>
                <ul>
                  {[
                    'Portfolio Performance Summary & Growth',
                    'Drawdown History & Recovery Analysis',
                    'Trade Duration vs Outcome Analysis',
                    'R/R Execution Quality Metrics',
                    'Win Rate & Breakeven Analysis',
                    'Beta vs S&P 500 Market Comparison',
                    'Diversification & Concentration Analysis',
                    'Asset Correlation Matrix Visualization',
                    'Robustness & Fragility Testing',
                    'Trade Removal Sensitivity Analysis',
                    'Cost & Slippage Sensitivity Testing',
                    'Strategy Score & Rating System',
                    'Market Regime Classification',
                    'Bootstrap & Permutation Testing',
                    'Monte Carlo Simulations',
                    'Trader Action Plan (Prioritized Next Steps)',
                    'Interactive Table of Contents',
                    'Comprehensive Educational Glossary'
                  ].map((item, idx) => (
                    <li key={idx}>
                      <CheckCircle size={18} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <button 
                onClick={() => { setProduct('trading'); handleNavClick('upload'); }} 
                className="cta-button"
                style={{ cursor: 'pointer', marginTop: '20px' }}
              >
                Get Started (Trader)
              </button>
            </div>

            {/* Investor product removed - platform now focuses on Trader (MT5) and Custom Analytics */}

            <div className="product-card product-custom" style={{ flex: '1', minWidth: '500px', maxWidth: '520px', background: 'linear-gradient(135deg, #0d2416 0%, #0f3020 100%)', borderColor: '#f59e0b' }}>
              <div className="product-header">
                <Zap className="product-icon" size={48} style={{ color: '#f59e0b' }} />
                <h2 className="product-name" style={{ color: '#f59e0b' }}>Custom Analytics</h2>
              </div>
              <h3 className="product-title">Tailored Solutions for Any Data Challenge</h3>
              <p className="product-desc">
                Custom analytics solutions tailored to your unique needs. Whether it's business data, operational metrics, or specialized analysis—we build data-driven reports and dashboards for any industry or use case.
              </p>
              <div className="product-features">
                <h4>What We Offer:</h4>
                <ul>
                  {[
                    'Business Performance Analytics',
                    'Data-Driven Reporting & Dashboards',
                    'Financial & Operational Metrics',
                    'Risk Assessment & Monitoring',
                    'Custom PDF Report Generation',
                    'Statistical Analysis & Validation',
                    'Process Optimization Insights',
                    'Tailored to Your Industry',
                    'Consultation & Strategy',
                    'Integration with Your Systems',
                    'Ongoing Support & Updates',
                    'Competitive Advantage Through Data'
                  ].map((item, idx) => (
                    <li key={idx}>
                      <CheckCircle size={18} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <button 
                onClick={() => handleNavClick('contact')} 
                className="cta-button"
                style={{ cursor: 'pointer', marginTop: '20px', background: '#f59e0b' }}
              >
                Contact Us for a Quote
              </button>
            </div>
          </div>
        </div>
      )}

      {activePage === 'mt5-permutation-testing' && (
        <div className="page">
          <h1 className="page-title">MT5 Permutation Testing</h1>
          <div className="section">
            <p className="section-text">
              AnalyticClarity provides MetaTrader 5 (MT5) permutation testing to validate
              whether your strategy performance is robust or driven by randomness. We analyze
              MT5 reports and run permutation testing, trade removal sensitivity, and Monte
              Carlo simulations to stress test your edge.
            </p>
            <p className="section-text">
              Our MT5 analytics include strategy tester style diagnostics, strategy analysis,
              drawdown behavior, and custom financial analytics that translate directly into
              actionable improvements.
            </p>
            <button
              onClick={() => handleNavClick('upload')}
              className="cta-button"
              style={{ cursor: 'pointer', marginTop: '20px' }}
            >
              Upload MT5 Report
            </button>
          </div>
        </div>
      )}

      {activePage === 'metatrader-5-analytics' && (
        <div className="page">
          <h1 className="page-title">MetaTrader 5 Analytics</h1>
          <div className="section">
            <p className="section-text">
              Get comprehensive MetaTrader 5 analytics with clear MT5 report insights,
              performance diagnostics, and strategy validation. We convert raw MT5 trade
              history into professional-grade analytics tailored for traders and prop firms.
            </p>
            <p className="section-text">
              From custom analytics to financial analytics, we focus on the metrics that
              matter: risk, drawdown, expectancy, and robustness testing.
            </p>
            <button
              onClick={() => handleNavClick('products')}
              className="cta-button"
              style={{ cursor: 'pointer', marginTop: '20px' }}
            >
              Explore Analytics Products
            </button>
          </div>
        </div>
      )}

      {activePage === 'samples' && (
        <div className="page samples-page">
          <div className="samples-container">
            <div className="samples-header">
              <h1>Examples & Sample Reports</h1>
              <p>Choose your analytics type to view sample reports and get started.</p>
              <p className="samples-description">
                Our comprehensive analytics reports deliver institutional-grade performance analysis, 
                statistical validation, and actionable insights tailored to your trading or investment style. 
                Explore the sample reports below to see the depth of analysis, professional visualizations, and clear metrics 
                that help you make data-driven decisions.
              </p>
            </div>

            <div className="samples-cards">
              <div className="sample-card trader-card">
                <div className="card-header">
                  <BarChart3 className="card-icon" size={32} />
                  <span className="card-label">Trader</span>
                </div>
                <h2>Trader (MT5)</h2>
                <p>Advanced statistical analysis for active traders using MetaTrader 5 data. 
                   Includes performance metrics, robustness testing, and detailed trade analysis.</p>
                <ul className="card-features">
                  <li>Trade-level diagnostics</li>
                  <li>Statistical robustness testing</li>
                  <li>Monte Carlo simulations</li>
                  <li>Market regime analysis</li>
                </ul>
                <button 
                  className="view-sample-btn trader-btn"
                  onClick={() => setSelectedSample('trading')}
                >
                  View Sample
                </button>
              </div>

              {/* Investor sample removed */}
            </div>

            {selectedSample && (
              <div className={`pdf-viewer-container ${sampleFullscreen ? 'fullscreen' : ''}`}>
                <div className="pdf-floating-controls">
                  <button 
                    className="expand-btn"
                    onClick={() => setSampleFullscreen(prev => !prev)}
                    title={sampleFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                  >
                    {sampleFullscreen ? 'Collapse' : 'Expand'}
                  </button>
                  <button 
                    className="close-btn"
                    onClick={() => {
                      setSelectedSample(null);
                      setSampleFullscreen(false);
                    }}
                    title="Close preview"
                  >
                    ✕
                  </button>
                </div>
                <div className="pdf-viewer">
                  <div className="pdf-frame">
                    <iframe
                      src={`${process.env.PUBLIC_URL}/pdf's of examples/mt5_example.pdf#toolbar=0`}
                      type="application/pdf"
                      width="100%"
                      height="100%"
                      title={`trading sample`}
                      style={{ 
                        border: 'none',
                        borderRadius: '0'
                      }}
                      onContextMenu={(e) => e.preventDefault()}
                      onDragStart={(e) => e.preventDefault()}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activePage === 'upload' && (
        <div className="page upload-page">
          <h1 className="page-title">Generate Your Analytics Report</h1>
          <p className="page-subtitle">Choose a product, upload the correct CSV, and get a PDF tailored to that workflow.</p>

          <div className="product-toggle" role="tablist" aria-label="Choose analysis type">
            <button
              className={`product-tab active`}
              role="tab"
              aria-selected={true}
              aria-pressed={true}
              tabIndex={0}
              title="Trader (MT5)"
              onClick={() => setProduct('trading')}
            >
              Trader (MT5)
            </button>
          </div>

          <p className="page-subtitle" style={{ marginTop: '10px', textAlign: 'center' }}>
            Trader: Upload your MetaTrader 5 trade history CSV for full performance analytics.
          </p>

          {!pdfReady && (
            <div className={`upload-area ${uploadedFile ? 'has-file' : ''}`}>
              <Upload className="upload-icon" size={64} />
              <h3 className="upload-title">
                {uploadedFile ? uploadedFile.name : 'Drop your CSV file here'}
              </h3>
              <p className="upload-subtitle">
                {uploadedFile ? 'File ready for processing' : 'Supports brokerage exports and standard trade history formats'}
              </p>
              {uploadedFile ? (
                <div className="upload-actions">
                  <label className="change-file-button" style={{ cursor: 'pointer' }}>
                    Change File
                    <input type="file" accept=".csv" onChange={handleFileUpload} className="hidden-input" />
                  </label>
                  <button onClick={resetUpload} className="remove-file-button" style={{ cursor: 'pointer' }}>
                    <X size={20} />
                    Remove
                  </button>
                </div>
              ) : (
                <label className="choose-file-button" style={{ cursor: 'pointer' }}>
                  Choose File
                  <input type="file" accept=".csv" onChange={handleFileUpload} className="hidden-input" />
                </label>
              )}
            </div>
          )}

          {error && (
            <div className="error-message">
              <AlertCircle size={24} />
              <span>{error}</span>
            </div>
          )}

          {uploadedFile && !isProcessing && !pdfReady && (
            <button onClick={processAndGeneratePDF} className="generate-button" style={{ cursor: 'pointer' }}>
              <FileText size={24} />
              Generate Trader Report
            </button>
          )}

          {isProcessing && (
            <div className="processing-card">
              <Loader className="processing-spinner" size={64} />
              <h3 className="processing-title">Analyzing Your Portfolio</h3>
              <p className="processing-status">{processingStatus}</p>
              <div className="progress-bar">
                <div className="progress-fill"></div>
              </div>
            </div>
          )}

          {pdfReady && (
            <div className="success-card">
              <div className="success-header">
                <CheckCircle className="success-icon" size={64} />
                <div>
                  <h2 className="success-title">Report Ready!</h2>
                  <p className="success-subtitle">Your comprehensive trading analysis is complete</p>
                </div>
              </div>
              
              <div className="report-info">
                <h3>Your Report Contains:</h3>
                <ul>
                  <li>Executive Summary with Portfolio Classification</li>
                  <li>Trade Attribution & Performance Drivers</li>
                  <li>Behavioral Diagnostics & Risk Analysis</li>
                  <li>Monte Carlo Simulations & Outcome Ranges</li>
                  <li>Statistical Stability Assessments</li>
                  <li>AI-Powered Optimization Insights</li>
                </ul>
              </div>

              <button
                onClick={downloadPDF}
                className="download-button"
                style={{ cursor: 'pointer' }}
              >
                <Download size={24} />
                Download PDF Report
              </button>

              <button onClick={resetUpload} className="analyze-another-button" style={{ cursor: 'pointer' }}>
                Analyze Another Portfolio
              </button>
            </div>
          )}

          <div className="csv-requirements">
            <h3>Required CSV Fields:</h3>
            <p><strong>Trader (MT5):</strong> Time, Position, Symbol, Type, Volume, Price, Commission/Swap, Profit.</p>
            <p><strong>Optional:</strong> Stop Loss, Take Profit for richer stats.</p>
          </div>

          <div className="csv-instructions">
            <div className="csv-instruction-card">
              <h4>MetaTrader 5 — how to export</h4>
              <ol>
                <li>Open MetaTrader 5 and go to the <strong>Toolbox</strong> (or <strong>Terminal</strong>) panel at the bottom.</li>
                <li>Click the <strong>History</strong> tab.</li>
                <li>Right-click anywhere in the history list and select <strong>Report</strong> → <strong>Export to Excel (XLSX)</strong>.</li>
                <li>Choose your date range (Last year, or Custom for full coverage) and save the .xlsx file to your computer.</li>
                <li>Open the .xlsx file in Excel (or Google Sheets).</li>
                <li>In Excel/Sheets, go to <strong>File</strong> → <strong>Save As</strong> (or <strong>Download</strong>) → select <strong>CSV (Comma delimited)</strong> and save.</li>
                <li>Upload the saved CSV file here.</li>
              </ol>
              <p className="csv-note">Ensure closed trades, commissions, swaps, and profits are present in your export. If you see extra header rows or blank lines, remove them before uploading.</p>
            </div>
          </div>
        </div>
      )}

      {activePage === 'contact' && (
        <div className="page contact-page">
          <h1 className="page-title">Get In Touch</h1>
          <p className="page-subtitle">Questions about our analytics? Need custom reporting? We're here to help.</p>
          <div className="contact-form">
            <div className="form-group">
              <label>Name</label>
              <input 
                type="text" 
                placeholder="Your name"
                value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                disabled={contactLoading}
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input 
                type="email" 
                placeholder="your@email.com"
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                disabled={contactLoading}
              />
            </div>
            <div className="form-group">
              <label>Message</label>
              <textarea 
                rows="6" 
                placeholder="Tell us about your needs..."
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                disabled={contactLoading}
              ></textarea>
            </div>
            {contactMessage && (
              <div className={`contact-message ${contactMessage.type}`}>
                {contactMessage.type === 'success' ? <CheckCircle size={24} /> : <AlertCircle size={24} />}
                <span>{contactMessage.text}</span>
              </div>
            )}
            <button
              onClick={handleContactSubmit}
              className="submit-button"
              style={{ cursor: 'pointer' }}
              disabled={contactLoading}
            >
              {contactLoading ? (
                <span className="button-content">
                  <Loader size={24} className="processing-spinner" style={{ display: 'inline', marginRight: '8px' }} />
                  Sending...
                </span>
              ) : (
                <span className="button-content">
                  <Mail size={24} />
                  Send Message
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {activePage === 'policies' && (
        <div className="page policies-page">
          <h1 className="page-title">Policies</h1>
          <p className="page-subtitle">
            Everything you need to know about our services and policies
          </p>
          <div className="policy-grid">
            {/* Free Access Card */}
            <div className="policy-card">
              <div className="policy-card-header">
                <CreditCard size={32} style={{ color: '#ffd600' }} />
                <h2>Free Access</h2>
              </div>
              <div className="policy-card-content">
                <p>
                  All reports are <strong>free</strong> to generate and download. There are no fees,
                  subscriptions, or one-time payments required.
                </p>
              </div>
            </div>

            {/* Refund Policy Card */}
            <div className="policy-card">
              <div className="policy-card-header">
                <CheckCircle size={32} style={{ color: '#ffd600' }} />
                <h2>Refund Policy</h2>
              </div>
              <div className="policy-card-content">
                <p>
                  Since the service is free, no refunds are necessary. If you encounter issues or inaccurate
                  results, please contact us and we will investigate and fix the problem.
                </p>
              </div>
            </div>

            {/* Cancellation Policy Card */}
            <div className="policy-card">
              <div className="policy-card-header">
                <X size={32} style={{ color: '#ffd600' }} />
                <h2>Cancellation Policy</h2>
              </div>
              <div className="policy-card-content">
                <p>
                  There are <strong>no subscriptions</strong> or recurring charges. No cancellations are required.
                </p>
              </div>
            </div>

            {/* Terms of Service Card */}
            <div className="policy-card">
              <div className="policy-card-header">
                <FileText size={32} style={{ color: '#ffd600' }} />
                <h2>Terms of Service</h2>
              </div>
              <div className="policy-card-content">
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginBottom: '1rem', paddingLeft: '1.5rem', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#ffd600', fontWeight: 'bold' }}>✓</span>
                    We do not store, retain, or share your uploaded data. All files are processed in-memory and deleted after analysis.
                  </li>
                  <li style={{ marginBottom: '1rem', paddingLeft: '1.5rem', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#ffd600', fontWeight: 'bold' }}>✓</span>
                    We never share or sell your data to third parties.
                  </li>
                  <li style={{ marginBottom: '1rem', paddingLeft: '1.5rem', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#ffd600', fontWeight: 'bold' }}>✓</span>
                    By using this service, you agree to provide accurate data and understand that results depend on the quality of your input.
                  </li>
                  <li style={{ paddingLeft: '1.5rem', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#ffd600', fontWeight: 'bold' }}>✓</span>
                    This service does not constitute financial advice. Use the results at your own discretion.
                  </li>
                </ul>
              </div>
            </div>

            {/* Privacy Policy Card */}
            <div className="policy-card">
              <div className="policy-card-header">
                <Lock size={32} style={{ color: '#ffd600' }} />
                <h2>Privacy Policy</h2>
              </div>
              <div className="policy-card-content">
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginBottom: '1rem', paddingLeft: '1.5rem', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#ffd600', fontWeight: 'bold' }}>🔒</span>
                    We do not store your uploaded files or personal data after your report is generated.
                  </li>
                  <li style={{ marginBottom: '1rem', paddingLeft: '1.5rem', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#ffd600', fontWeight: 'bold' }}>🔒</span>
                    We do not send your data to any third parties.
                  </li>
                  <li style={{ paddingLeft: '1.5rem', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#ffd600', fontWeight: 'bold' }}>🔒</span>
                    Your privacy and security are our top priorities.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default TradeAnalyticsPlatform;